import React__default, { useContext, useMemo, useState, useEffect } from 'react';
import { ThemeContext } from 'styled-components';
import { g as getMatchMediaFunction } from './getMatchMediaFunction-DzNKmYsy.js';
import { M as MobileMode } from './MobileMode-DHJ6VHqt.js';
import { M as MobileModeChangeSource } from './MobileModeChangeSource-CL9HhYGS.js';
import { M as MobileModeHint } from './MobileModeHint-BwA4f8WD.js';
import { M as MobileThemeContext } from './MobileThemeContext-ILBGocWR.js';
import { u as useDefaultStateManager } from './useDefaultStateManager-PmeX2qPE.js';

const mobileMediaQuery = '(orientation: portrait) and (max-width: 575.98px) and (pointer: coarse), (orientation: landscape) and (max-height: 575.98px) and (pointer: coarse)';
const notMobileMediaQuery = 'all and (orientation: portrait) and (min-width: 576px), all and (orientation: landscape) and (min-height: 576px), all and (pointer: fine)';
function MobileThemeProvider(props) {
  var _mobile$mediaQuery, _mobile;
  const {
    mobileMode = MobileMode.Auto,
    mobileModeHint = MobileModeHint.Mobile,
    onMobileModeChange: externalOnMobileModeChange,
    children
  } = props;
  const defaultTheme = useContext(ThemeContext);

  /* use the mediaQuery from the theme context if it is available */
  const mediaQuery = (_mobile$mediaQuery = defaultTheme === null || defaultTheme === void 0 || (_mobile = defaultTheme.mobile) === null || _mobile === void 0 ? void 0 : _mobile.mediaQuery) !== null && _mobile$mediaQuery !== void 0 ? _mobile$mediaQuery : mobileMediaQuery;

  /* Create a matchMedia object. Recreate it if the mediaQuery changes */
  const isMobileMatchMedia = useMemo(() => getMatchMediaFunction(mobileModeHint)(mediaQuery), [mediaQuery, mobileModeHint]);
  const [browserIsMobile, setBrowserIsMobile] = useState(isMobileMatchMedia.matches);
  const {
    isMobile: autoIsMobile,
    onMobileModeChange: defaultOnMobileModeChange
  } = useDefaultStateManager(isMobileMatchMedia.matches);

  /* Use the external state manager when it is available */
  const onMobileModeChange = externalOnMobileModeChange !== null && externalOnMobileModeChange !== void 0 ? externalOnMobileModeChange : defaultOnMobileModeChange;
  useEffect(() => {
    /* setup a listener for browser change its mobile/not-mobile status */
    const isMobileMatchMediaListener = event => {
      /* only fire a onMobileModeChange if mobileMode has changed */
      setBrowserIsMobile(event.matches);
      onMobileModeChange({
        source: MobileModeChangeSource.Browser,
        mobileMode: event.matches ? MobileMode.Enable : MobileMode.Disable
      });
    };
    isMobileMatchMedia.addListener(isMobileMatchMediaListener);

    /* Fire a change event on mount if the hint does not match what the browser detected */
    if (isMobileMatchMedia.matches !== (mobileModeHint === MobileModeHint.Mobile)) {
      setBrowserIsMobile(isMobileMatchMedia.matches);
      isMobileMatchMediaListener({
        matches: isMobileMatchMedia.matches
      });
    }
    return () => {
      /* cleanup match media event listener */
      isMobileMatchMedia.removeListener(isMobileMatchMediaListener);
    };
  }, [isMobileMatchMedia, onMobileModeChange, mobileModeHint]);

  /* Use internal logic to pick mobile/not-mobile in "Auto" mode */
  let isMobile = autoIsMobile;

  /* use the mobileMode if it is set to "Enable" or "Disable" */
  if (mobileMode === MobileMode.Enable) {
    isMobile = true;
  } else if (mobileMode === MobileMode.Disable) {
    isMobile = false;
  }

  /* memoize complex context values to prevent unneeded re-renders */
  const mobileThemeContextValue = useMemo(() => ({
    isMobile,
    onMobileModeChange,
    browserIsMobile
  }), [isMobile, onMobileModeChange, browserIsMobile]);

  /* Create the mobile theme and memoize it */
  const mobileTheme = useMemo(() => {
    var _mobile$mediaQuery2, _mobile2, _mobile$inverseMediaQ, _mobile3;
    return {
      ...defaultTheme,
      mobile: {
        ...defaultTheme.mobile,
        mode: mobileMode,
        mediaQuery: (_mobile$mediaQuery2 = defaultTheme === null || defaultTheme === void 0 || (_mobile2 = defaultTheme.mobile) === null || _mobile2 === void 0 ? void 0 : _mobile2.mediaQuery) !== null && _mobile$mediaQuery2 !== void 0 ? _mobile$mediaQuery2 : mobileMediaQuery,
        inverseMediaQuery: (_mobile$inverseMediaQ = defaultTheme === null || defaultTheme === void 0 || (_mobile3 = defaultTheme.mobile) === null || _mobile3 === void 0 ? void 0 : _mobile3.inverseMediaQuery) !== null && _mobile$inverseMediaQ !== void 0 ? _mobile$inverseMediaQ : notMobileMediaQuery
      }
    };
  }, /* regenerate the theme if incoming theme or mode changes */
  [mobileMode, defaultTheme]);
  return /*#__PURE__*/React__default.createElement(ThemeContext.Provider, {
    value: mobileTheme
  }, /*#__PURE__*/React__default.createElement(MobileThemeContext.Provider, {
    value: mobileThemeContextValue
  }, children));
}

export { MobileThemeProvider as M };
//# sourceMappingURL=MobileThemeProvider-BIJoqee3.js.map
